import { useState, useRef, useCallback } from 'react'
import type React from 'react'
import type { ResultPill, VideoSource } from '../../components/v2/pv2-shared'
import type { CompiledQuery } from '../../core/components/v2/compiled-query-types'
import { parsePrompt } from '../../core/lib/api/parse'
import { getDemoVideoUrl } from '../../core/lib/api/misc'
import { provisionAnonKey } from '../../core/lib/api/anon-key'
import { hasAcceptedTerms } from '../../contexts/PrimateAuthContext'
import type { NarrativeEntry } from '../../lib/narrative'
import { trackStreamPromptUpdated } from '../../core/telemetry/events'

interface StreamSession {
  connectionState: string
  sendPromptUpdate: (prompt: string) => void
  requestCameraAccess: () => Promise<void>
  startStream: (prompt: string, token: string | null, tier: string, samEnabled: boolean) => Promise<void>
  startFileStream: (
    source: File | Blob,
    prompt: string,
    token: string | null,
    tier: string,
    samEnabled: boolean,
  ) => Promise<void>
}

/**
 * useSubmitHandlers — Phase 5 (PRI-291)
 *
 * Extracts handleSubmit() and its supporting functions from PrimateVisionV2Inner:
 *   - startFlyAnimation (internal helper)
 *   - requireTermsAccepted
 *   - handleTermsAccepted
 *   - handleSubmit (unified submit handler with 3 routing paths)
 *
 * Also owns showTermsForAction state (returned for JSX use).
 * Called from PrimateVisionV2Inner which is outside V2Providers, so all
 * dependencies are passed directly.
 *
 * CRITICAL: Pre-connect check (streamSession.connectionState === 'pre_connected')
 * is preserved exactly — it is timing-sensitive. requireTermsAccepted fires before
 * any stream/upload starts.
 */
/**
 * embedCompiledQuery — when the user has edited the structured query lanes
 * (CompiledQueryCard "Done"), serialize the override into the prompt string
 * using the |||COMPILED_QUERY:<base64>||| envelope that the inference server
 * extracts in primate_stream_inference.py (~line 999).
 *
 * Without this, mid-session prompt edits via the lane editor never reach the
 * model — the raw text prompt is re-sent unchanged and the PromptParser
 * re-derives the original subjects. The lane override is silently lost.
 *
 * Returns the prompt unchanged when there's no compiled-query override.
 */
export function embedCompiledQuery(prompt: string, compiledQuery: CompiledQuery | null | undefined): string {
  if (!compiledQuery) return prompt
  try {
    const json = JSON.stringify(compiledQuery)
    // btoa requires latin-1; encodeURIComponent + unescape is the canonical
    // browser-safe utf-8 → base64 trick.
    const b64 = (typeof btoa === 'function')
      ? btoa(unescape(encodeURIComponent(json)))
      : Buffer.from(json, 'utf-8').toString('base64')
    return prompt + '|||COMPILED_QUERY:' + b64 + '|||'
  } catch (err) {
    // Defensive: if the encode fails for any reason, fall back to the raw
    // prompt so the user always gets *something* through.
    console.warn('[useSubmitHandlers] failed to embed compiled query, sending raw prompt', err)
    return prompt
  }
}

const OPEN_ENDED_SUBMIT_PATTERNS = [
  /^what\b.*(see|visible|there|happening|going on|in (the|this) (frame|video|scene)|around)/i,
  /^tell\s+(?:me\s+)?(?:what|what.s)/i,
  /^describe\b/i,
  /^what'?s (happening|going on|in (the|this) (frame|video|scene))/i,
  /^(narrate|explain (what|the scene|what.s))/i,
  /^(what|what.s)\s*[?!.]?\s*$/i,
]

function shouldParseAtSubmit(prompt: string, compiledQuery: CompiledQuery | null | undefined): boolean {
  if (compiledQuery) return false
  const cleaned = prompt.trim()
  return OPEN_ENDED_SUBMIT_PATTERNS.some(pattern => pattern.test(cleaned))
}

export function useSubmitHandlers({
  // Prompt + source state
  prompt,
  videoSource,
  processOffline,
  isPro,
  samEnabled,
  // Selected media
  selectedFile,
  selectedDemoVideo,
  selectedSavedVideo,
  demoVideos,
  // State setters
  setIsStreaming,
  setStreamErrorState,
  setActivePrompt,
  setPromptSent,
  setResultPills,
  setAfterState,
  setCarouselVisible,
  setOfflineJobActive,
  // Refs (fly animation)
  submittedThumbRef,
  streamPillIdRef,
  streamHistoryRef,
  narrativeRef,
  flyThumbnailRef,
  capturedCamElRef,
  camRef,
  camRectRef,
  // Auth
  getToken,
  isSignedIn,
  onAnonTrialExhausted,
  onAnonProvisioningFailed,
  // Stream session
  streamSession,
  // Handler from useAnalysis
  handleAnalyseWithPrompt,
  // Compiled query — passed through to analysis when lanes are visible
  compiledQuery,
}: {
  prompt: string
  videoSource: VideoSource | null
  processOffline: boolean
  isPro: boolean
  samEnabled: boolean
  selectedFile: File | null
  selectedDemoVideo: string | null
  selectedSavedVideo: { id: string } | null
  demoVideos: Array<{ id: string; display_name: string; filename: string; thumbnail_url?: string | null }>
  setIsStreaming: (v: boolean) => void
  setStreamErrorState: (s: 'idle' | 'error' | 'retrying') => void
  setActivePrompt: (p: string) => void
  setPromptSent: (v: boolean) => void
  setResultPills: React.Dispatch<React.SetStateAction<ResultPill[]>>
  setAfterState: (v: boolean) => void
  setCarouselVisible: (v: boolean) => void
  setOfflineJobActive: (v: boolean) => void
  submittedThumbRef: React.MutableRefObject<string | null>
  streamPillIdRef: React.MutableRefObject<string | null>
  streamHistoryRef: React.MutableRefObject<Array<{ frame_num: number; answer: string; confidence: number; elapsed_s?: number; term_confidences?: Record<string, number>; recording_elapsed_ms?: number }>>
  narrativeRef: React.MutableRefObject<NarrativeEntry[]>
  flyThumbnailRef: React.MutableRefObject<string | null>
  capturedCamElRef: React.MutableRefObject<HTMLElement | null>
  camRef: React.RefObject<HTMLDivElement | null>
  camRectRef: React.MutableRefObject<DOMRect | null>
  getToken: ((options?: { template?: string }) => Promise<string | null>) | null | undefined
  /** PRI-503: Clerk session present. When false, submit provisions the anon
      free-grant key (user-intent trigger — never on page load; IP-capped). */
  isSignedIn?: boolean
  /** PRI-503: called when anon provisioning hits the sandbox/upgrade IP cap
      (typed codes sandbox_limit_exceeded / upgrade_limit_exceeded) — the
      caller shows the friendly "free trial used, sign up" state. */
  onAnonTrialExhausted?: () => void
  /** Called when anon key provisioning fails for any OTHER reason (network
      blip, transient provisioning error, cold start, etc.) — the caller
      shows a friendly "couldn't start your trial, try again" prompt instead
      of letting the underlying request fire with a null token (which the
      server rejects with a raw auth error). */
  onAnonProvisioningFailed?: () => void
  streamSession: StreamSession
  handleAnalyseWithPrompt: (prompt: string, compiledQuery?: CompiledQuery | null) => void
  compiledQuery?: CompiledQuery | null
}) {
  // ── Terms modal state ─────────────────────────────────────────────────────
  const [showTermsForAction, setShowTermsForAction] = useState(false)
  const pendingTermsCallbackRef = useRef<(() => void) | null>(null)

  // ── requireTermsAccepted ──────────────────────────────────────────────────
  const requireTermsAccepted = useCallback((callback: () => void): boolean => {
    if (hasAcceptedTerms()) return true
    pendingTermsCallbackRef.current = callback
    setShowTermsForAction(true)
    return false
  }, [])

  // ── handleTermsAccepted ───────────────────────────────────────────────────
  const handleTermsAccepted = useCallback(() => {
    setShowTermsForAction(false)
    localStorage.setItem('primate-terms-accepted', '1')
    const cb = pendingTermsCallbackRef.current
    pendingTermsCallbackRef.current = null
    if (cb) cb()
  }, [])

  // ── resolveSubmitToken ───────────────────────────────────────
  // PRI-503: bearer for this submit. Signed-in → Clerk session token.
  // Anonymous → get-or-provision the anon free-grant pv_live_ key at the
  // moment of user intent (sandbox → upgrade; idempotent + single-flight in
  // anon-key.ts). IP-cap / exhaustion errors surface via onAnonTrialExhausted.
  // Any OTHER provisioning failure (network blip, transient error, cold
  // start) surfaces via onAnonProvisioningFailed — the caller must NOT fire
  // the underlying request with a null token, which previously reached the
  // server and came back as a raw "Authentication failed" error deep in the
  // product UI (2026-08-25 prospect-facing incident — see
  // /tmp/plan-login-auth-guard-2026-08-25.md).
  async function resolveSubmitTokenDetailed(): Promise<{ token: string | null; exhausted: boolean; provisioningFailed: boolean }> {
    // Anon provisioning only when the caller EXPLICITLY says signed-out
    // (isSignedIn === false). Undefined = legacy callers/tests → getToken path.
    if (isSignedIn === false) {
      try {
        const anon = await provisionAnonKey()
        return { token: anon.apiKey, exhausted: false, provisioningFailed: false }
      } catch (err) {
        const code = (err as { code?: string })?.code
        const status = (err as { status?: number })?.status
        if (code === 'sandbox_limit_exceeded' || code === 'upgrade_limit_exceeded' || status === 429) {
          onAnonTrialExhausted?.()
          return { token: null, exhausted: true, provisioningFailed: false }
        }
        console.warn('[useSubmitHandlers] anon key provisioning failed — surfacing friendly retry state', err)
        onAnonProvisioningFailed?.()
        return { token: null, exhausted: false, provisioningFailed: true }
      }
    }
    if (getToken) {
      try { return { token: await getToken().catch(() => null), exhausted: false, provisioningFailed: false } } catch { return { token: null, exhausted: false, provisioningFailed: false } }
    }
    // No getToken (dev bypass) — no bearer.
    return { token: null, exhausted: false, provisioningFailed: false }
  }


  // ── startFlyAnimation ─────────────────────────────────────────────────────
  // Shared pre-animation setup for all three submit paths.
  function startFlyAnimation(
    pillId: string,
    submittedPrompt: string,
    thumbUrl: string | null,
  ) {
    flyThumbnailRef.current = thumbUrl
    capturedCamElRef.current = camRef.current
    if (camRef.current) {
      camRectRef.current = camRef.current.getBoundingClientRect()
    }
    streamPillIdRef.current = pillId
    streamHistoryRef.current = []
    narrativeRef.current = []
    setResultPills(prev => [...prev, {
      id: pillId,
      prompt: submittedPrompt,
      confidence: 0,
      resultText: '',
      videoThumb: thumbUrl,
      streamHistory: [],
      narrative: [] as NarrativeEntry[],
      searchTerms: [],
    } as ResultPill])
    setAfterState(true)
    setCarouselVisible(true)
  }

  // ── handleSubmit ──────────────────────────────────────────────────────────
  function handleSubmit() {
    if (!prompt.trim()) return
    if (!requireTermsAccepted(() => handleSubmit())) return
    // Bare text the user typed — used for UI surfaces (pill label, activePrompt)
    const rawPrompt = prompt.trim()
    let submittedPrompt = embedCompiledQuery(rawPrompt, compiledQuery)
    submittedThumbRef.current = videoSource?.thumbUrl ?? null

    async function resolveSubmittedPrompt(): Promise<string> {
      if (!shouldParseAtSubmit(rawPrompt, compiledQuery)) return submittedPrompt
      try {
        const parsed = await parsePrompt(rawPrompt)
        return embedCompiledQuery(rawPrompt, parsed.compiled_query)
      } catch (err) {
        console.warn('[useSubmitHandlers] submit-time parse failed, sending raw prompt', err)
        return submittedPrompt
      }
    }

    // Camera streaming path
    if (!videoSource || videoSource.kind === 'stream') {
      const activeStates = ['live', 'recalculating', 'quota_warning', 'degraded']
      if (activeStates.includes(streamSession.connectionState)) {
        const sendPromptUpdate = (wirePrompt: string) => {
          streamSession.sendPromptUpdate(wirePrompt)
          trackStreamPromptUpdated({ prompt: rawPrompt, user_plan: isPro ? 'pro' : 'free' })
          setActivePrompt(rawPrompt)
          if (streamPillIdRef.current) {
            const pillId = streamPillIdRef.current
            setResultPills(prev => prev.map(p => p.id === pillId ? { ...p, prompt: rawPrompt } : p))
          }
          setPromptSent(true)
          setTimeout(() => setPromptSent(false), 600)
        }
        if (shouldParseAtSubmit(rawPrompt, compiledQuery)) {
          void (async () => {
            submittedPrompt = await resolveSubmittedPrompt()
            sendPromptUpdate(submittedPrompt)
          })()
        } else {
          sendPromptUpdate(submittedPrompt)
        }
        return
      }

      setStreamErrorState('idle')
      // activePrompt is a UI surface (rendered in MobileLayout heading,
      // DesktopLayout pill, RightDataPanel JSON tray, MobileStreamingHUD
      // bottom bar) AND is written into pill.prompt by useStreamLifecycle
      // on fallback / displayedResult merge paths. NEVER set the wire format.
      // The wire-format (submittedPrompt) is passed separately to startStream
      // below so the compiled-query override still reaches inference.
      setActivePrompt(rawPrompt)

      // Pill is a UI surface — rawPrompt only. Wire-format (submittedPrompt)
      // goes to streamSession.startStream below; the two MUST stay separate.
      // Regression guard: V2ResultPill.stripCompiledQueryEnvelope is the
      // defense-in-depth boundary, but we strip at the source here so the
      // pill.prompt and any DB persistence path see clean text.
      const pendingPillId = `stream-${Date.now()}`
      startFlyAnimation(pendingPillId, rawPrompt, null)
      setIsStreaming(true)

      const tier = isPro ? 'pro' : 'free'
      const doStream = async () => {
        await streamSession.requestCameraAccess()
        const { token, exhausted, provisioningFailed } = await resolveSubmitTokenDetailed()
        if (exhausted || provisioningFailed) { setIsStreaming(false); return } // trial-exhausted / provisioning-failed overlay is up
        submittedPrompt = await resolveSubmittedPrompt()
        await streamSession.startStream(submittedPrompt, token, tier, samEnabled)
        setIsStreaming(true)
      }
      void doStream()
      return
    }

    // File video path — Live mode (captureStream)
    if (!processOffline) {
      const activeStates = ['live', 'recalculating', 'quota_warning', 'degraded']
      if (activeStates.includes(streamSession.connectionState)) {
        const sendPromptUpdate = (wirePrompt: string) => {
          streamSession.sendPromptUpdate(wirePrompt)
          // Mirror the camera-stream branch: prompt updates on file streams are
          // the same user action and must be tracked identically (eval finding).
          trackStreamPromptUpdated({ prompt: rawPrompt, user_plan: isPro ? 'pro' : 'free' })
          setActivePrompt(rawPrompt)
          if (streamPillIdRef.current) {
            const pillId = streamPillIdRef.current
            setResultPills(prev => prev.map(p => p.id === pillId ? { ...p, prompt: rawPrompt } : p))
          }
          setPromptSent(true)
          setTimeout(() => setPromptSent(false), 600)
        }
        if (shouldParseAtSubmit(rawPrompt, compiledQuery)) {
          void (async () => {
            submittedPrompt = await resolveSubmittedPrompt()
            sendPromptUpdate(submittedPrompt)
          })()
        } else {
          sendPromptUpdate(submittedPrompt)
        }
        return
      }

      if (streamSession.connectionState !== 'idle') {
        console.log('[PV] handleSubmit: file stream already in progress (state=%s), ignoring', streamSession.connectionState)
        return
      }
      setStreamErrorState('idle')
      // See camera-path note above — activePrompt is a UI surface and feeds
      // pill.prompt on fallback paths. Always store rawPrompt.
      setActivePrompt(rawPrompt)

      // Pill display uses rawPrompt (no envelope); wire path uses submittedPrompt.
      const pendingPillId = `stream-${Date.now()}`
      startFlyAnimation(pendingPillId, rawPrompt, videoSource?.thumbUrl ?? null)
      setIsStreaming(true)

      const tier = isPro ? 'pro' : 'free'
      const doFileStream = async () => {
        await new Promise(r => setTimeout(r, 800))
        const { token, exhausted, provisioningFailed } = await resolveSubmitTokenDetailed()
        if (exhausted || provisioningFailed) { setIsStreaming(false); return } // trial-exhausted / provisioning-failed overlay is up

        if (selectedFile) {
          submittedPrompt = await resolveSubmittedPrompt()
          await streamSession.startFileStream(selectedFile, submittedPrompt, token, tier, samEnabled)
        } else if (selectedDemoVideo) {
          const dv = demoVideos.find(v => v.id === selectedDemoVideo)
          if (dv) {
            // R15 (PRI-439): endpoint literal moved to the API layer.
            const videoUrl = getDemoVideoUrl(dv.filename)
            try {
              const response = await fetch(videoUrl)
              if (!response.ok) throw new Error('Failed to fetch demo video')
              const blob = await response.blob()
              submittedPrompt = await resolveSubmittedPrompt()
              await streamSession.startFileStream(blob, submittedPrompt, token, tier, samEnabled)
            } catch (err) {
              console.error('[handleSubmit] Failed to fetch demo video for streaming:', err)
              setStreamErrorState('error')
              setIsStreaming(false)
              handleAnalyseWithPrompt(submittedPrompt, compiledQuery)
            }
          }
        } else if (selectedSavedVideo) {
          console.log('[handleSubmit] Saved video streaming not yet supported, using offline')
          setIsStreaming(false)
          setStreamErrorState('idle')
          handleAnalyseWithPrompt(submittedPrompt, compiledQuery)
        }
      }
      void doFileStream()
      return
    }

    // File video path — Batch mode (upload pipeline)
    // Pill display uses rawPrompt (no envelope); wire path (handleAnalyseWithPrompt)
    // uses submittedPrompt so the compiled-query override still reaches the API.
    // PRI-503: for anon users, provision the trial key BEFORE dispatching — the
    // upload path authenticates via the token registry, which falls back to the
    // stored anon key once it exists.
    const offlinePillId = `offline-${Date.now()}`
    startFlyAnimation(offlinePillId, rawPrompt, videoSource?.thumbUrl ?? null)
    setOfflineJobActive(true)
    setTimeout(() => {
      if (isSignedIn === false) {
        // PRI-503 anon: provision the trial key BEFORE dispatching — the upload
        // path authenticates via the token registry, which falls back to the
        // stored anon key once it exists.
        void resolveSubmitTokenDetailed().then(({ exhausted, provisioningFailed }) => {
          // Trial exhausted / provisioning failed → the overlay is up; don't fire a doomed upload.
          if (exhausted || provisioningFailed) { setOfflineJobActive(false); return }
          handleAnalyseWithPrompt(submittedPrompt, compiledQuery)
        })
        return
      }
      handleAnalyseWithPrompt(submittedPrompt, compiledQuery)
    }, 800)
  }

  return {
    handleSubmit,
    requireTermsAccepted,
    handleTermsAccepted,
    showTermsForAction,
  }
}
